import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PushPinIcon from "@mui/icons-material/PushPin";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { StyleSettings } from "../../styles/StyleSettings";
import MenuInfoPopup from "@widgets/commonpopup/MenuInfoPopup";

/**
 * 탭은 가로로만 움직여야 한다.
 * 세로 이동을 허용하면 탭이 탭 바 밖으로 끌려 내려가고, 그 과정에서
 * 화면이 세로로 자동 스크롤된다. (@dnd-kit/modifiers 패키지가 없어 직접 정의)
 */
const restrictToHorizontalAxis: Modifier = ({ transform }) => ({
  ...transform,
  y: 0,
});

/** 드래그 시작 시점의 탭 목록 좌우 끝 (첫 탭 왼쪽 ~ 마지막 탭 오른쪽) */
type TabListBounds = { left: number; right: number };

export interface TabItem {
  key: string; // Unique menu code
  title: string | React.ReactNode; // Tab title
  originTitle: string; // Original Tab title
  component: React.JSX.Element; // Tab content
  closeable: boolean; // Indicates if the tab is closeable
  ref?: React.Ref<any>;
}

interface TabBarProps {
  tabs: TabItem[];
  activeTab: number;
  /** 우클릭 위치 계산에 쓰이는 탭 영역 DOM 참조 (MainLayout이 보유) */
  containerRef: React.RefObject<HTMLDivElement>;
  onContextMenu: (event: React.MouseEvent) => void;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  onTabClick: (event: React.MouseEvent, index: number) => void;
  onTabClose: (index: number) => void;
  /** 탭 활성화 — 드래그를 시작한 탭을 곧바로 보고 있는 탭으로 만든다 */
  onTabActivate: (index: number) => void;
  /** 탭 순서 이동 — tabs/menuDatas/componentRefs/pathMap을 함께 옮긴다 */
  onMoveTab: (from: number, to: number) => void;
  /** 핀 고정된 탭의 메뉴코드 목록(우클릭 메뉴에서 토글) */
  pinnedKeys?: string[];
}

interface SortableTabProps {
  tab: TabItem;
  index: number;
  isPinned?: boolean;
  onTabClose: (index: number) => void;
  onTabDoubleClick: (tab: TabItem) => void;
  /** MUI Tabs가 cloneElement로 주입하는 값들(selected, value, onChange 등)이 들어온다 */
  [key: string]: any;
}

/**
 * 드래그로 순서를 바꿀 수 있는 탭 하나.
 *
 * MUI Tabs는 자식을 cloneElement로 복제하면서 선택 상태(selected)·value 등을
 * 주입한다. 그래서 이 컴포넌트는 받은 props를 그대로 안쪽 Tab에 넘겨야 한다.
 */
function SortableTab({
  tab,
  index,
  isPinned,
  onTabClose,
  onTabDoubleClick,
  ...tabProps
}: SortableTabProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.key });

  return (
    <Tab
      ref={setNodeRef}
      style={{
        // 가로 목록이라 좌우 이동만 쓴다(Transform을 쓰면 크기까지 변형된다)
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 2 : undefined,
        position: "relative",
      }}
      {...attributes}
      {...listeners}
      {...tabProps}
      // dnd-kit이 role="button"을 붙이는데, 탭 우클릭 메뉴가
      // querySelectorAll('[role="tab"]')로 탭을 찾으므로 반드시 되돌린다.
      role="tab"
      onDoubleClick={() => onTabDoubleClick(tab)}
      sx={{
        width: 130,
        padding: "0px 7px !important", // Tab 자체에 패딩
        fontSize: `${StyleSettings.font}px`,
        marginTop: "13px",
        marginLeft: "10px",
        minHeight: "35px",
      }}
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          {isPinned && (
            <PushPinIcon
              sx={{ fontSize: "small", mr: 0.5, color: "white" }}
              titleAccess="핀 고정됨"
            />
          )}
          <Box
            sx={{
              display: "inline-block", // 텍스트 영역을 인라인 블록으로 설정
              overflow: "hidden", // 넘치는 텍스트를 숨기기
              textOverflow: "ellipsis", // 넘칠 때 ... 표시
              whiteSpace: "nowrap", // 텍스트 줄 바꿈 방지
              width: tab.closeable ? "calc(100% - 20px)" : "100%",
              fontWeight: "bold",
              textAlign: "left",
            }}
          >
            {tab.title}
          </Box>
          {tab.closeable && (
            <CloseIcon
              sx={{
                ml: 1,
                fontSize: "small",
                cursor: "pointer",
                color: "white",
                "&:hover": { color: "white" },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onTabClose(index);
              }}
            />
          )}
        </Box>
      }
    />
  );
}

/**
 * 상단 탭 바.
 *
 * MainLayout에서 분리한 이유:
 * 탭 순서 드래그처럼 "탭 바 안에서만 의미 있는 상태"를 MainLayout에 두면
 * 마우스가 움직일 때마다 열려 있는 모든 화면이 다시 그려진다.
 * 여기서 상태를 갖고 있으면 리렌더가 탭 바 안에서 끝난다.
 */
export default function TabBar({
  tabs,
  activeTab,
  pinnedKeys = [],
  containerRef,
  onContextMenu,
  onTabChange,
  onTabClick,
  onTabClose,
  onTabActivate,
  onMoveTab,
}: TabBarProps) {
  // 탭 더블클릭 시 뜨는 메뉴 정보 팝업 (탭 바 안에서만 쓰인다)
  const [selectedTabInfo, setSelectedTabInfo] = useState<any | null>(null);

  // 드래그 범위를 "탭 목록의 실제 좌우 끝"으로 잡는다.
  // 부모 요소 영역으로 제한하면 탭이 많아 가로 스크롤이 생겼을 때
  // 화면 밖에 있는 마지막 자리까지 갈 수 없다.
  const boundsRef = useRef<TabListBounds | null>(null);

  const captureTabListBounds = () => {
    const container = containerRef.current;
    const tabEls = container?.querySelectorAll<HTMLElement>('[role="tab"]');
    if (!tabEls || tabEls.length === 0) {
      boundsRef.current = null;
      return;
    }
    boundsRef.current = {
      left: tabEls[0].getBoundingClientRect().left,
      right: tabEls[tabEls.length - 1].getBoundingClientRect().right,
    };
  };

  const handleDragStart = (event: DragStartEvent) => {
    captureTabListBounds();

    // 드래그를 시작한 탭을 활성화한다.
    // 5px 이상 움직이면 클릭이 발생하지 않아, 이게 없으면 다른 탭을 보면서
    // 엉뚱한 탭을 옮기게 된다(브라우저 탭은 누르는 순간 활성화된다).
    const index = tabs.findIndex((tab) => tab.key === event.active.id);
    if (index !== -1 && index !== activeTab) {
      onTabActivate(index);
    }
  };

  /** 드래그 중인 탭을 탭 목록의 좌우 끝 안쪽으로 가둔다 */
  const restrictToTabList = useCallback<Modifier>(
    ({ draggingNodeRect, transform }) => {
      const bounds = boundsRef.current;
      if (!draggingNodeRect || !bounds) return transform;

      const value = { ...transform };
      if (draggingNodeRect.left + transform.x < bounds.left) {
        value.x = bounds.left - draggingNodeRect.left;
      } else if (draggingNodeRect.right + transform.x > bounds.right) {
        value.x = bounds.right - draggingNodeRect.right;
      }
      return value;
    },
    []
  );

  // 5px 이상 움직여야 드래그로 인정한다.
  // 이게 없으면 탭 클릭·더블클릭·닫기(X) 버튼이 드래그로 먹혀버린다.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const tabKeys = useMemo(() => tabs.map((tab) => tab.key), [tabs]);

  const handleTabDoubleClick = (tab: TabItem) => {
    setSelectedTabInfo({
      ...tab,
      LastModified: new Date().toLocaleString(), // 필요하면 추가 정보 넣기
    });
  };

  // 드래그하는 동안 이웃 탭이 밀려나는 건 dnd-kit이 CSS transform으로 처리하므로,
  // 실제 배열 이동은 손을 뗄 때 한 번만 한다.
  // (드래그 중에 배열을 바꾸면 열려 있는 모든 화면이 매 프레임 다시 그려진다)
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = tabs.findIndex((tab) => tab.key === active.id);
    const to = tabs.findIndex((tab) => tab.key === over.id);
    if (from === -1 || to === -1) return;

    onMoveTab(from, to);
  };

  return (
    <div
      ref={containerRef}
      onContextMenu={onContextMenu}
      style={{ zIndex: 1, width: "100%", overflowX: "hidden" }}
    >
      {selectedTabInfo && (
        <MenuInfoPopup
          info={selectedTabInfo}
          onClose={() => setSelectedTabInfo(null)}
        />
      )}
      {/* 왼쪽: TabList */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis, restrictToTabList]}
        // 자동 스크롤은 탭 바의 가로 스크롤에만 쓴다.
        // 세로 임계값을 0으로 두면 화면이 위아래로 끌려가지 않는다.
        autoScroll={{ threshold: { x: 0.2, y: 0 } }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={tabKeys} strategy={horizontalListSortingStrategy}>
          <Tabs
            // 홈 화면일 때는 활성 탭이 없다(-1). 그대로 넘기면 MUI가
            // "매칭되는 탭이 없다"고 경고하므로 false로 바꿔 준다.
            value={activeTab < 0 ? false : activeTab}
            onChange={onTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              ".MuiTab-root": {
                color: "white", // 모든 탭의 글자색을 흰색으로 설정
                backgroundColor: "rgb(151, 151, 151)", // 비활성화된 탭의 배경색
                "&.Mui-selected": {
                  backgroundColor: "rgb(11, 12, 105)", // 활성화된 탭의 배경색
                  color: "white", // 활성화된 탭의 글자색을 흰색으로 설정
                },
              },
              ".MuiTabs-indicator": {
                display: "none", // 인디케이터를 숨김
              },
              flexShrink: 0, // 탭이 축소되지 않도록 설정
            }}
          >
            {tabs.map((tab, index) => (
              <SortableTab
                key={tab.key}
                value={index}
                tab={tab}
                index={index}
                isPinned={pinnedKeys.includes(tab.key)}
                onClick={(e: React.MouseEvent) => onTabClick(e, index)}
                onTabClose={onTabClose}
                onTabDoubleClick={handleTabDoubleClick}
              />
            ))}
          </Tabs>
        </SortableContext>
      </DndContext>
    </div>
  );
}
